import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  ScanLine,
  ShieldCheck,
  CalendarClock,
  Send,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Mail,
} from "lucide-react";
import { DoxMascot } from "@/components/Logo";

/**
 * LiveWorkflowDemo — a scripted, looping 45-second demonstration of Dox
 * doing real work. Replaces the static DashboardMock in the Hero.
 *
 * The animation walks a first-time visitor through the entire employee
 * loop so they leave believing they just watched an AI do their paperwork.
 *
 * Timing (ms cumulative):
 *   0     → owner uploads Texas TDLR electrical license PDF
 *   3500  → Dox scans, extracted fields populate
 *   9000  → missing COI detected (red chip)
 *   13000 → Dox requests the COI from carrier
 *   17500 → status flips red → green
 *   21500 → deadline added to compliance calendar
 *   27500 → renewal packet prepared
 *   33000 → submitted to Texas TDLR
 *   38000 → "You're compliant. I'll monitor everything from here."
 *   45000 → loop restart
 */

const STEPS = [
  { at: 0, key: "upload", label: "Uploading contractor license", tone: "info" },
  { at: 3500, key: "analyze", label: "Dox is reviewing the license…", tone: "info" },
  { at: 9000, key: "missing", label: "Missing Certificate of Insurance detected", tone: "warn" },
  { at: 13000, key: "request", label: "Dox requested a fresh COI from your carrier", tone: "info" },
  { at: 17500, key: "green", label: "Insurance verified — you're covered", tone: "ok" },
  { at: 21500, key: "deadline", label: "Renewal deadline added to Compliance Calendar", tone: "info" },
  { at: 27500, key: "packet", label: "Renewal packet prepared for TDLR", tone: "info" },
  { at: 33000, key: "submit", label: "Filed.", tone: "ok" },
  { at: 38000, key: "done", label: "You're compliant. I'm monitoring from here.", tone: "ok" },
  { at: 45000, key: "loop", label: "restart", tone: "info" },
];

const TOTAL_MS = 45000;

export default function LiveWorkflowDemo() {
  const [tick, setTick] = useState(0);
  const [paused, setPaused] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      if (elapsed >= TOTAL_MS) {
        startRef.current = Date.now();
        setTick(0);
      } else {
        setTick(elapsed);
      }
    }, 120);
    return () => clearInterval(id);
  }, [paused]);

  const progress = Math.min(100, (tick / TOTAL_MS) * 100);
  const currentIndex = STEPS.reduce((idx, s, i) => (tick >= s.at ? i : idx), -1);
  const currentKey = STEPS[currentIndex]?.key;

  // Boolean states used to reveal UI chrome across the sequence.
  const showUpload = tick >= STEPS[0].at;
  const analyzing = tick >= STEPS[1].at && tick < STEPS[2].at;
  const showExtracted = tick >= STEPS[1].at + 1200;
  const missingCOI = tick >= STEPS[2].at && tick < STEPS[4].at;
  const requestingCOI = tick >= STEPS[3].at && tick < STEPS[4].at;
  const coiGreen = tick >= STEPS[4].at;
  const showDeadline = tick >= STEPS[5].at;
  const showPacket = tick >= STEPS[6].at;
  const submitted = tick >= STEPS[7].at;
  const doneBanner = tick >= STEPS[8].at;

  const replay = () => {
    startRef.current = Date.now();
    setTick(0);
  };

  return (
    <div
      data-testid="live-workflow-demo"
      className="glass-strong rounded-2xl border border-white/10 p-3 sm:p-4 shadow-2xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="rounded-xl bg-[#0A0713] border border-white/5 overflow-hidden">
        {/* Chrome header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          <div className="ml-4 text-xs text-white/40 font-mono">ezdoxai.com/dashboard</div>
          <div className="ml-auto flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${paused ? "bg-white/30" : "bg-emerald-400 animate-pulse"}`} />
            <span className="text-[10px] text-white/50 font-medium uppercase tracking-wider">
              {paused ? "Paused" : "Live"}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-white/5 relative">
          <div
            className="h-full bg-gradient-to-r from-dox-orange via-dox-magenta to-dox-purple transition-[width] duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="grid grid-cols-12 min-h-[420px] sm:min-h-[460px]">
          {/* Dox side panel */}
          <div className="col-span-12 sm:col-span-4 border-r border-white/5 p-4 sm:p-5 flex flex-col gap-3 bg-black/30">
            <div className="flex items-start gap-2.5">
              <div className="relative shrink-0">
                <DoxMascot size={32} />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-black" />
              </div>
              <div>
                <div className="font-display font-black text-white text-sm leading-tight">Dox</div>
                <div className="text-[10px] text-emerald-300 leading-tight">
                  Working now · your AI Employee
                </div>
              </div>
            </div>

            {/* Live commentary */}
            <div className="mt-1 text-xs text-white/70 leading-relaxed min-h-[68px]">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentKey}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-dox-orange font-semibold">Dox: </span>
                  {commentary(currentKey)}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Timeline */}
            <div className="mt-2 space-y-1.5 flex-1 overflow-hidden">
              {STEPS.slice(0, 9).map((s, i) => {
                const done = tick >= s.at;
                const active = i === currentIndex;
                return (
                  <div
                    key={s.key}
                    className={`flex items-start gap-2 text-[11px] transition-all ${
                      done ? "opacity-100" : "opacity-30"
                    }`}
                  >
                    <span
                      className={`mt-0.5 h-3 w-3 rounded-full shrink-0 transition-colors ${
                        s.tone === "ok" && done
                          ? "bg-emerald-400"
                          : s.tone === "warn" && done
                          ? "bg-amber-400"
                          : done
                          ? "bg-dox-orange"
                          : "bg-white/20"
                      } ${active ? "ring-2 ring-dox-orange/40 animate-pulse" : ""}`}
                    />
                    <span className={`leading-snug ${active ? "text-white font-medium" : "text-white/70"}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              data-testid="workflow-replay"
              onClick={replay}
              className="mt-2 text-[11px] text-white/50 hover:text-white transition-colors self-start inline-flex items-center gap-1.5"
            >
              <Sparkles size={11} /> Watch again
            </button>
          </div>

          {/* Main stage */}
          <div className="col-span-12 sm:col-span-8 p-4 sm:p-5 relative">
            {/* Upload card */}
            <AnimatePresence>
              {showUpload && !showExtracted && (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  className="rounded-xl border border-dashed border-dox-orange/40 bg-dox-orange/5 p-5 flex items-center gap-3"
                >
                  <FileText size={28} className="text-dox-orange" />
                  <div>
                    <div className="text-sm font-semibold text-white">TDLR-Electrical-License.pdf</div>
                    <div className="text-[11px] text-white/50 mt-0.5">1.2 MB · uploaded moments ago</div>
                  </div>
                  {analyzing && (
                    <div className="ml-auto flex items-center gap-2 text-[11px] text-dox-orange">
                      <ScanLine size={14} className="animate-pulse" />
                      Analyzing
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Extracted fields */}
            <AnimatePresence>
              {showExtracted && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl bg-white/[0.03] border border-white/10 p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-white/60" />
                      <span className="text-xs font-semibold text-white">TDLR Electrical License</span>
                    </div>
                    <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-500/10 px-2 py-0.5 rounded-full">
                      Extracted · 96% confidence
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <Field label="License #" value="TDLR-EC-284916" />
                    <Field label="Holder" value="BuildCo LLC" />
                    <Field label="Issued" value="Aug 2024" />
                    <Field label="Expires" value="Aug 15, 2026" />
                    <Field label="Jurisdiction" value="Texas" />
                    <Field label="Bond" value="$25,000 verified" />
                  </div>

                  {/* Compliance chips */}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Chip tone="ok" icon={ShieldCheck} label="License valid" />
                    <Chip tone="ok" icon={ShieldCheck} label="Bond on file" />
                    <AnimatePresence mode="wait">
                      {missingCOI && (
                        <motion.div
                          key="missing"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-300 px-2.5 py-1 text-[10px] font-semibold"
                        >
                          <AlertTriangle size={11} /> COI missing
                          {requestingCOI && <span className="ml-1 opacity-75">· requesting…</span>}
                        </motion.div>
                      )}
                      {coiGreen && (
                        <motion.div
                          key="ok"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-2.5 py-1 text-[10px] font-semibold"
                        >
                          <CheckCircle2 size={11} /> COI verified
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Calendar deadline card */}
            <AnimatePresence>
              {showDeadline && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3"
                >
                  <div className="h-9 w-9 rounded-lg bg-dox-orange/15 flex items-center justify-center text-dox-orange">
                    <CalendarClock size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-white">Renewal deadline · Aug 15, 2026</div>
                    <div className="text-[10px] text-white/45">Added to Compliance Calendar · 56 days out</div>
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-400">✓ Synced</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Renewal packet */}
            <AnimatePresence>
              {showPacket && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-3 rounded-xl border p-3 flex items-center gap-3 transition-colors ${
                    submitted
                      ? "bg-emerald-500/10 border-emerald-400/30"
                      : "bg-white/[0.03] border-white/10"
                  }`}
                >
                  <div
                    className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                      submitted ? "bg-emerald-500/20 text-emerald-300" : "bg-dox-magenta/15 text-dox-magenta"
                    }`}
                  >
                    {submitted ? <Send size={16} /> : <FileText size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-white">
                      {submitted ? "Filed with TDLR" : "Renewal packet · TDLR-EC-284916"}
                    </div>
                    <div className="text-[10px] text-white/45">
                      {submitted ? "Confirmation: TX-RN-8471 · Dox is monitoring" : "Forms, COI, bond rider — all attached"}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      submitted
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-dox-magenta/15 text-dox-magenta"
                    }`}
                  >
                    {submitted ? "✓ Filed" : "Ready"}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Done banner */}
            <AnimatePresence>
              {doneBanner && (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-dox-purple/20 border border-emerald-400/30 p-4 flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-full bg-emerald-500/25 border border-emerald-400/40 flex items-center justify-center">
                    <CheckCircle2 size={20} className="text-emerald-300" />
                  </div>
                  <div>
                    <div className="font-display font-black text-white text-base leading-tight">
                      You&apos;re compliant.
                    </div>
                    <div className="text-xs text-emerald-200/90 leading-tight mt-0.5">
                      I&apos;ll monitor everything from here.
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Carrier email micro-toast (COI request) */}
            <AnimatePresence>
              {requestingCOI && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute bottom-3 right-3 max-w-[220px] glass border border-white/10 rounded-xl px-3 py-2 flex items-start gap-2"
                >
                  <Mail size={14} className="text-dox-magenta mt-0.5 shrink-0" />
                  <div className="text-[10px] text-white/80 leading-snug">
                    <div className="font-semibold text-white">Emailing carrier</div>
                    <div className="text-white/50">Requesting a fresh COI on your behalf…</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function commentary(key) {
  switch (key) {
    case "upload":
      return "I see you just handed me your Texas electrical license. Let me get my team on it.";
    case "analyze":
      return "My Document Specialist is reading the PDF — pulling the license number, holder, expiration…";
    case "missing":
      return "My Insurance Specialist noticed you're missing a current Certificate of Insurance. She'll go get one.";
    case "request":
      return "My Communications Specialist is emailing your carrier to request a fresh COI — no action needed from you.";
    case "green":
      return "COI is on file. You're fully covered.";
    case "deadline":
      return "My Calendar Specialist marked Aug 15, 2026 on your Compliance Calendar so we don't miss the renewal.";
    case "packet":
      return "My Licensing Specialist is assembling the renewal packet — forms, COI, bond rider, everything TDLR needs.";
    case "submit":
      return "My Filing Specialist just sent the package to TDLR and has the confirmation number.";
    case "done":
      return "You're compliant. My team is monitoring everything from here.";
    default:
      return "Team is standing by.";
  }
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-[11px] font-medium text-white truncate">{value}</div>
    </div>
  );
}

function Chip({ tone, icon: Icon, label }) {
  const tones = {
    ok: "bg-emerald-500/15 text-emerald-300",
    warn: "bg-amber-500/15 text-amber-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${tones[tone]}`}
    >
      <Icon size={11} /> {label}
    </span>
  );
}
